import { useEffect, useState } from 'react';
import { createBrowserRouter, RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import * as Sentry from '@sentry/react';

import { ErrorReport } from '@/components/errors/error-report';
import { useAuth, useResolvePartnerParams } from '@/lib/auth';

/**
 * Type for router creation functions
 */
type RouterCreator = (
  routes: RouteObject[],
  options?: Record<string, unknown>
) => ReturnType<typeof createBrowserRouter>;

/**
 * Creates a browser router with consistent configuration options
 * @param routes - The routes to configure the router with
 * @param routerCreator - Function to create the router (defaults to createBrowserRouter)
 * @returns A configured router instance
 */
const createConfiguredRouter = (
  routes: RouteObject[],
  routerCreator: RouterCreator = createBrowserRouter
) => {
  return routerCreator(routes);
};

/**
 * Creates a router for unauthenticated users
 * Dynamically imports and configures routes for users who are not logged in
 * @param routerCreator - Function to create the router (defaults to createBrowserRouter)
 * @returns Promise that resolves to a configured router
 */
const createUnauthenticatedRouter = async (routerCreator?: RouterCreator) => {
  const { routesUnauthenticated } = await import('./routes-unauthenticated');
  return createConfiguredRouter(routesUnauthenticated, routerCreator);
};

/**
 * Creates a router for authenticated users (SurfOps routes are gated by AdminRequired)
 * @param routerCreator - Function to create the router (defaults to createBrowserRouter)
 * @returns Promise that resolves to a configured router
 */
const createAuthenticatedRouter = async (routerCreator?: RouterCreator) => {
  const [routesAuthenticated, routesUnauthenticated] = await Promise.all([
    import('./routes').then((m) => m.routes),
    import('./routes-unauthenticated').then((m) => m.routesUnauthenticated),
  ]);

  return createConfiguredRouter(
    [...routesAuthenticated, ...routesUnauthenticated],
    routerCreator
  );
};

/**
 * Synchronous versions of route combination functions for testing
 * These return the same route combinations as the async versions but without dynamic imports
 */
export const getUnauthenticatedRoutes = async () => {
  // Import synchronously for testing
  const { routesUnauthenticated } = await import('./routes-unauthenticated');
  return routesUnauthenticated;
};

export const getAuthenticatedRoutes = async () => {
  const [routes, routesUnauthenticated] = await Promise.all([
    import('./routes').then((m) => m.routes),
    import('./routes-unauthenticated').then((m) => m.routesUnauthenticated),
  ]);
  return [...routes, ...routesUnauthenticated];
};

/** @deprecated Use getAuthenticatedRoutes — SurfOps lives in the main tenant route tree. */
export const getAdminRoutes = getAuthenticatedRoutes;

/**
 * LazyRouterProvider component that dynamically creates and provides routers based on user authentication state
 *
 * This component handles two router configurations:
 * - Unauthenticated users: Basic routes for login/signup
 * - Authenticated users: Tenant-prefixed routes (SurfOps is gated by AdminRequired)
 *
 * The router is created lazily to reduce initial bundle size and only loads
 * the routes that are actually needed based on the user's permissions.
 *
 * @param routerCreator - Function to create the router (defaults to createBrowserRouter for production)
 * @returns RouterProvider component with the appropriate router configuration, or loading/error states
 */
export const LazyRouterProvider = ({
  routerCreator = createBrowserRouter,
}: {
  routerCreator?: RouterCreator;
} = {}) => {
  const { user, loading } = useAuth();
  const [router, setRouter] = useState<ReturnType<
    typeof createBrowserRouter
  > | null>(null);
  const [routerLoading, setRouterLoading] = useState(true);
  const [routerError, setRouterError] = useState<Error | null>(null);

  useResolvePartnerParams();

  useEffect(() => {
    const createRouter = async () => {
      setRouterLoading(true);
      setRouterError(null);
      try {
        const newRouter = user
          ? await createAuthenticatedRouter(routerCreator)
          : await createUnauthenticatedRouter(routerCreator);

        setRouter(newRouter);
      } catch (error) {
        const errorInstance =
          error instanceof Error ? error : new Error(String(error));
        setRouterError(errorInstance);
        Sentry.captureException(error);
      } finally {
        setRouterLoading(false);
      }
    };

    // Only recreate router if user authentication status changes (not tenant changes)
    // This prevents flicker during tenant switching
    if (!loading) {
      createRouter();
    }
  }, [user?.id, loading, routerCreator]);

  if (loading || routerLoading) {
    return null;
  }

  if (routerError) {
    return <ErrorReport error={routerError} />;
  }

  if (!router) {
    return (
      <ErrorReport
        error={new Error('Router initialization failed: No router found')}
      />
    );
  }

  return <RouterProvider router={router} />;
};
